// App-wide "you've hit your limit" upgrade modal.
//
// Mounted once via <UpgradeModalProvider> (App.tsx). Feature pages call
// `useUpgradeModal()` and either open it directly or feed it a failed API
// error via `openFromError` — QUOTA_EXCEEDED (402) bodies carry featureKey,
// used, limit and upgradeTier from the server. Reuses the EXISTING upgrade
// flows: Free users go to /pricing to subscribe; active Cosmic subscribers go
// to /pricing?upgrade=celestial which auto-opens the Stripe proration preview.
// Cosmic Pro (celestial/enterprise) users never see this modal.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { Crown, Sparkles, Check, Gauge, Building2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/hooks/useAuth";
import { getEffectiveTier } from "@/lib/subscription";
import { apiRequest } from "@/lib/queryClient";

export const FEATURE_LABELS: Record<string, string> = {
  compatibility_match: "Compatibility matches",
  consultation_booking: "Consultations",
  kundali_pdf: "Kundali PDF downloads",
  tarot_reading: "Tarot readings",
};

const PLAN_INFO: Record<string, { name: string; price: string; benefits: string[] }> = {
  cosmic: {
    name: "Cosmic",
    price: "$9.99/mo",
    benefits: [
      "Unlimited compatibility matches",
      "1 human + 2 AI consultations / month",
      "3 Kundali PDF downloads / month",
      "Tarot readings (3 per day)",
      "Full birth chart with AI insights",
      "Numerology profile & compatibility",
    ],
  },
  celestial: {
    name: "Cosmic Pro",
    price: "$19.99/mo",
    benefits: [
      "Unlimited compatibility matches",
      "2 human + 5 AI consultations / month",
      "Unlimited Kundali PDF downloads",
      "Unlimited tarot readings",
      "AI Relationship Coach",
      "Team & family compatibility analysis",
    ],
  },
};

export interface UpgradeModalInfo {
  featureKey?: string;
  featureName?: string; // overrides FEATURE_LABELS lookup
  used?: number;
  limit?: number;
  upgradeTier?: string | null; // server suggestion; defaults by current tier
  message?: string;
  continuation?: {
    source: "personal" | "organization";
    consultationType?: "human" | "ai";
    organizationId?: string;
    organizationName?: string;
    ownerPath?: string;
    requestPath?: string;
    upgradePath?: string;
    resetAt?: string;
    personalBillingBlocked?: boolean;
  };
}

interface UpgradeModalContextValue {
  open: (info: UpgradeModalInfo) => void;
  /** Open from a failed apiRequest error. Returns true when handled (402 quota/tier). */
  openFromError: (err: unknown, featureName?: string) => boolean;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(null);

export function useUpgradeModal(): UpgradeModalContextValue {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) throw new Error("useUpgradeModal must be used within UpgradeModalProvider");
  return ctx;
}

export function UpgradeModalProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [info, setInfo] = useState<UpgradeModalInfo | null>(null);
  const [requestPending, setRequestPending] = useState(false);

  const effectiveTier = getEffectiveTier(user);
  const isPro = effectiveTier === "celestial" || effectiveTier === "enterprise";

  const open = useCallback(
    (next: UpgradeModalInfo) => {
      // Cosmic Pro users never see upgrade prompts.
      if (isPro && next.continuation?.source !== "organization") return;
      setInfo(next);
    },
    [isPro],
  );

  const openFromError = useCallback(
    (err: unknown, featureName?: string): boolean => {
      const e = err as any;
      const status = typeof e?.status === "number" ? e.status : undefined;
      const is402 = status === 402 || /^402\b/.test(String(e?.message ?? ""));
      if (!is402) return false;
      open({
        featureKey: e?.featureKey,
        featureName,
        used: typeof e?.used === "number" ? e.used : undefined,
        limit: typeof e?.limit === "number" ? e.limit : undefined,
        upgradeTier: e?.upgradeTier ?? e?.requiredTier ?? null,
        message: typeof e?.message === "string" ? e.message : undefined,
        continuation: e?.continuation,
      });
      return true;
    },
    [isPro, open],
  );

  const value = useMemo(() => ({ open, openFromError }), [open, openFromError]);

  // Default suggestion: Free → Cosmic, Cosmic → Cosmic Pro.
  const suggestedTier =
    info?.upgradeTier && PLAN_INFO[info.upgradeTier]
      ? info.upgradeTier
      : effectiveTier === "cosmic"
        ? "celestial"
        : "cosmic";
  const plan = PLAN_INFO[suggestedTier];

  const featureLabel =
    info?.featureName ?? (info?.featureKey ? FEATURE_LABELS[info.featureKey] : undefined) ?? "This feature";
  const hasUsage = typeof info?.used === "number" && typeof info?.limit === "number" && info.limit > 0;
  const notIncluded = typeof info?.limit === "number" && info.limit <= 0;
  const isConsultationFeature =
    info?.featureKey === "consultation_booking" ||
    info?.featureKey === "consultation_booking_human" ||
    info?.featureKey === "consultation_booking_ai";
  const organizationContinuation =
    info?.continuation?.source === "organization" ? info.continuation : undefined;

  const requestOrganizationAccess = async () => {
    if (!organizationContinuation?.requestPath) return;
    setRequestPending(true);
    try {
      await apiRequest("POST", organizationContinuation.requestPath, {
        consultationType: organizationContinuation.consultationType ?? "human",
      });
      setInfo(null);
      window.dispatchEvent(new CustomEvent("organization-consultation-requested"));
    } catch (error: any) {
      setInfo((current) => current
        ? { ...current, message: error?.message || "We couldn't send the request. Please try again." }
        : current);
    } finally {
      setRequestPending(false);
    }
  };

  const handleUpgrade = () => {
    setInfo(null);
    // Active Cosmic subscribers upgrade in place (Stripe proration preview);
    // everyone else lands on the normal subscribe flow.
    if (suggestedTier === "celestial" && effectiveTier === "cosmic") {
      setLocation("/pricing?upgrade=celestial");
    } else {
      setLocation("/pricing");
    }
  };

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      <Dialog open={!!info && (!isPro || !!organizationContinuation)} onOpenChange={(o) => !o && setInfo(null)}>
        <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {suggestedTier === "celestial" ? (
                <Crown className="h-5 w-5 text-primary" />
              ) : (
                <Sparkles className="h-5 w-5 text-primary" />
              )}
              {organizationContinuation
                ? `${organizationContinuation.organizationName ?? "Your organization"} manages consultation access`
                : notIncluded ? `${featureLabel} — locked on your plan` : `${featureLabel} limit reached`}
            </DialogTitle>
            <DialogDescription>
              {organizationContinuation
                ? info?.message ?? "Your organization has used the available consultation access for this month. Ask the owner for access, have them upgrade the organization plan, or wait for the monthly reset. Personal checkout is not available while your account is organization-managed."
                : notIncluded
                ? `${featureLabel} isn't included in your current plan. Upgrade to ${plan.name} to unlock it.`
                : info?.message ??
                  `You've used all of your ${featureLabel.toLowerCase()} for this period. Upgrade to ${plan.name} for more.${isConsultationFeature ? " Your consultation allowance resets monthly." : ""}`}
            </DialogDescription>
          </DialogHeader>

          {hasUsage && (
            <div className="rounded-lg border border-border/40 bg-secondary/30 p-3" data-testid="upgrade-modal-usage">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="flex items-center gap-1.5 font-medium">
                  <Gauge className="h-4 w-4 text-primary" />
                  {featureLabel}
                </span>
                <span className="text-muted-foreground">
                  {info!.used} / {info!.limit} used
                </span>
              </div>
              <Progress value={Math.min(100, (info!.used! / info!.limit!) * 100)} className="h-2 bg-secondary/60" />
            </div>
          )}

          {organizationContinuation ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="organization-quota-continuation">
              <div className="flex items-start gap-2">
                <Building2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div className="text-sm text-muted-foreground">
                  Ask the organization owner to assign another {organizationContinuation.consultationType ?? "human"} consultation, or have them upgrade the organization plan. Your personal billing cannot be used for this account.
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  variant="outline"
                  onClick={requestOrganizationAccess}
                  disabled={requestPending}
                  data-testid="organization-quota-request"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {requestPending ? "Sending request…" : "Ask owner for access"}
                </Button>
                {organizationContinuation.ownerPath && (
                  <Button
                    onClick={() => {
                      setInfo(null);
                      setLocation(organizationContinuation.ownerPath!);
                    }}
                    data-testid="organization-quota-owner-path"
                  >
                    Manage organization
                  </Button>
                )}
              </div>
            </div>
          ) : (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-2.5">
            <div className="flex items-baseline justify-between">
              <span className="font-semibold">{plan.name}</span>
              <span className="text-primary font-bold">{plan.price}</span>
            </div>
            <ul className="space-y-1.5">
              {plan.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setInfo(null)} data-testid="upgrade-modal-dismiss">
              Not now
            </Button>
            {!organizationContinuation && <Button
              onClick={handleUpgrade}
              className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 text-white hover:opacity-90"
              data-testid="upgrade-modal-cta"
            >
              Upgrade to {plan.name}
            </Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UpgradeModalContext.Provider>
  );
}
